import { Card, CardContent } from '@/components/ui/Card'
import { Text } from '@/components/ui/Text'
import type { ClientActivationState } from '@/lib/hooks/useMyActivationState'
import { Day0HorizontalStepper } from './Day0HorizontalStepper'
import { stepCaption } from '@/lib/onboarding/day0-view'

interface LastTransitionDisplay {
  /** Human-readable label for the transition cause (already mapped from machine key). */
  humanCause: string
  /** Relative time string, e.g. "3 days ago". */
  relativeTime: string
}

interface Day0StepperCardProps {
  stages: ClientActivationState['stages']
  /** 1-based index */
  currentStage: number
  /**
   * Chip text from waiting.targetText — only populated in awaiting-approval state, never on Day 0.
   * Keep the prop: it costs nothing when null and will render on ActivationWaitingScreen.
   * Takes priority over lastTransition when both are non-null.
   */
  nextMilestone: string | null
  /**
   * Human-readable last-transition event derived from debug.lastTransition.
   * Renders when nextMilestone is null and the cause key maps to known human copy.
   * Unknown cause → omitted (never render raw machine keys).
   */
  lastTransition?: LastTransitionDisplay | null
}

export function Day0StepperCard({
  stages,
  currentStage,
  nextMilestone,
  lastTransition = null,
}: Day0StepperCardProps) {
  const total = stages.length
  // Which stage the caption names is decided in `stepCaption` (day0-view.ts): a NEEDED stage, else — when the agreement is HELD — the
  // agreement step by its own position (a held agreement is served at currentStage 1, where `stages[currentStage - 1]` is the stage the
  // stepper already draws as completed), else the passed-in currentStage's own position. C13/T0062: never always index 0 — the agency's
  // "Step 2 of 3" must not read "Agency created".
  const { position, label: activeLabel } = stepCaption(stages, currentStage)

  const rightChip = nextMilestone
    ? { text: nextMilestone, color: 'primary' as const }
    : lastTransition
    ? { text: `${lastTransition.humanCause} · ${lastTransition.relativeTime}`, color: 'muted' as const }
    : null

  return (
    <Card data-testid="day0-stepper-card">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-1">
          <Text variant="label-sm" color="muted">
            Step {position} of {total} · {activeLabel}
          </Text>
          {rightChip && (
            <Text variant="label-sm" color={rightChip.color}>
              {rightChip.text}
            </Text>
          )}
        </div>
        <Text variant="body-sm" color="subtle" className="mb-5">
          Your account is being set up. Complete each step to go live.
        </Text>
        <Day0HorizontalStepper stages={stages} />
      </CardContent>
    </Card>
  )
}
